"use strict";

/**
 * Shared kalustohuolto (equipment maintenance) schedule computation.
 * Used by both the Cloud Function (getPublicKalustohuoltoAikataulu) and
 * the Vue components (Kalustonhuolto.vue, Ilmoitustaulu.vue).
 *
 * Self-contained: requires and extends the isoWeek dayjs plugin internally.
 */

const dayjs = require("dayjs");
const isoWeek = require("dayjs/plugin/isoWeek");
const advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

/**
 * @param {object} [options]
 * @param {string|null} [options.simulatedNow]  ISO date string to simulate a specific "now" (used in Kalustonhuolto dev tools)
 * @param {number}      [options.weeksAhead=19] How many weeks into the future to generate
 * @returns {{ week: string, weekNumber: string, startDate: string, endDate: string,
 *             startDateISO: string, endDateISO: string, group: number, engine: string, current: boolean }[]}
 */
function computeWeeksAndGroups(options) {
  const { simulatedNow, weeksAhead = 19 } = options || {};

  // Old era (before 2025-12-29): 5 maintenance groups, 4-slot engine cycle
  const oldEngines = ["511", "5112, 518, 5188", "513", "517, Pumppuperävaunu"];

  // New era (from 2025-12-29): 4 maintenance groups, 5-slot engine cycle
  const newEngines = [
    "511",
    "513, Asema",
    "5112",
    "517, Pumppuperävaunu, Asema",
    "518, 5188",
  ];

  const startDate = dayjs("2023-06-05");
  const switchDate = dayjs("2025-12-29");

  let now = dayjs(simulatedNow || undefined);
  if (now.isBefore(startDate)) {
    now = startDate;
  }
  const nowWeek = now.format("WW/GGGG");

  const endDate = now.add(weeksAhead, "week");
  const weeks = endDate.diff(startDate, "week");

  const result = [];
  try {
    for (let i = 0; i <= weeks; i++) {
      const d = startDate.add(i, "week");
      const week = d.format("WW/GGGG");

      let group;
      let engine;

      if (d.isBefore(switchDate)) {
        group = (i + 1) % 5 || 5;
        engine = oldEngines[i % 4];
      } else {
        const weeksSinceSwitch = d.diff(switchDate, "week");
        group = weeksSinceSwitch % 4 || 4;
        engine = newEngines[weeksSinceSwitch % 5];
      }

      result.push({
        week,
        weekNumber: d.format("GGGGWW"),
        startDate: d.startOf("isoWeek").format("D.M."),
        endDate: d.endOf("isoWeek").format("D.M."),
        startDateISO: d.startOf("isoWeek").format("YYYY-MM-DD"),
        endDateISO: d.endOf("isoWeek").format("YYYY-MM-DD"),
        group,
        engine,
        current: nowWeek === week,
      });
    }
  } catch (e) {
    console.error("computeWeeksAndGroups: schedule computation failed", e);
    return [];
  }

  return result;
}

/** @type {{ name: string, number: number, people: { title: string, name: string }[] }[]} */
const KALUSTOHUOLTO_GROUPS = [
  {
    name: "Ryhmä 1",
    number: 1,
    people: [
      { title: "Ryhmänjohtaja", name: "Heikkilä Kai" },
      { title: "Varajohtaja", name: "Latva Aimo" },
      { title: "Jäsen", name: "Haapaniemi Eero" },
      { title: "Jäsen", name: "Kankaristo Valtteri" },
      { title: "Jäsen", name: "Leinonen Janne" },
      { title: "Jäsen", name: "Jalava Juuso" },
      { title: "Jäsen", name: "Halme Jari" },
    ],
  },
  {
    name: "Ryhmä 2",
    number: 2,
    people: [
      { title: "Ryhmänjohtaja", name: "Vehkalahti Niko" },
      { title: "Varajohtaja", name: "Heiskala Matti" },
      { title: "Jäsen", name: "Heiskala Juha-Pekka" },
      { title: "Jäsen", name: "Salonen Tomi" },
      { title: "Jäsen", name: "Vehkalahti Max" },
      { title: "Jäsen", name: "Reponen Simo" },
      { title: "Jäsen", name: "Reponen Matti" },
      //{ title: "Jäsen", name: "Pentti Pasi" },
    ],
  },
  {
    name: "Ryhmä 3",
    number: 3,
    people: [
      { title: "Ryhmänjohtaja", name: "Rautiainen Joni" },
      { title: "Varajohtaja", name: "Yli-Anttila Tomi" },
      { title: "Jäsen", name: "Helin Jarkko" },
      { title: "Jäsen", name: "Peltomaa Joel" },
      { title: "Jäsen", name: "Uusikorpi Rami" },
      { title: "Jäsen", name: "Yli-Anttila Sara" },
      { title: "Jäsen", name: "Lähteenmäki Mika" },
    ],
  },
  {
    name: "Ryhmä 4",
    number: 4,
    people: [
      { title: "Ryhmänjohtaja", name: "Leino Joni" },
      { title: "Varajohtaja", name: "Heiskala Jani-Petteri" },
      { title: "Jäsen", name: "Leino Jouni" },
      { title: "Jäsen", name: "Toivanen Tuula" },
      { title: "Jäsen", name: "Pihlajamäki Pasi" },
      { title: "Jäsen", name: "Sartomäki Sami" },
      { title: "Jäsen", name: "Raikisto Mari" },
      { title: "Jäsen", name: "Ylinen Jarkko" },
      { title: "Jäsen", name: "Laitila Venla" },
    ],
  },
];

module.exports = { computeWeeksAndGroups, KALUSTOHUOLTO_GROUPS };
